import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Button, ButtonProps, Typography, alpha, useTheme, styled } from '@mui/material';
import { AlertTriangle, Home, ArrowLeft } from 'lucide-react';
import { PageContainer, MinimalCard } from 'components/styled';

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 80,
  height: 80,
  borderRadius: theme.spacing(2),
  background: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(239, 68, 68, 0.25)'
      : '1px solid rgba(239, 68, 68, 0.2)',
  color: theme.palette.error.main,
  marginBottom: theme.spacing(3),
}));

const StyledButton = styled(Button)<ButtonProps & { component?: React.ElementType; to?: string }>(
  ({ theme }) => ({
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.25, 3),
    borderRadius: theme.spacing(1),
  }),
);

function Page404() {
  const theme = useTheme();

  return (
    <PageContainer>
      <Helmet title="404 - Page Not Found" />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <MinimalCard
          $hoverEffect={false}
          sx={{
            maxWidth: 500,
            textAlign: 'center',
            p: { xs: 4, md: 6 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <IconContainer>
              <AlertTriangle size={40} />
            </IconContainer>

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '4rem', md: '5rem' },
                fontWeight: 700,
                color: theme.palette.text.primary,
                lineHeight: 1,
                mb: 1,
              }}
            >
              404
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1.5,
              }}
            >
              Page not found
            </Typography>

            <Typography
              sx={{
                color: alpha(theme.palette.text.secondary, 0.7),
                fontSize: '0.95rem',
                mb: 4,
                maxWidth: 350,
              }}
            >
              The page you&apos;re looking for doesn&apos;t exist or may have been moved.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <StyledButton
                component={Link}
                nativeButton={false}
                to="/"
                variant="contained"
                color="primary"
                startIcon={<Home size={18} />}
              >
                Go to Home
              </StyledButton>

              <StyledButton
                onClick={() => window.history.back()}
                variant="outlined"
                color="inherit"
                startIcon={<ArrowLeft size={18} />}
                sx={{
                  borderColor: alpha(theme.palette.text.primary, 0.2),
                  '&:hover': {
                    borderColor: alpha(theme.palette.text.primary, 0.4),
                    backgroundColor: alpha(theme.palette.text.primary, 0.05),
                  },
                }}
              >
                Go Back
              </StyledButton>
            </Box>
          </Box>
        </MinimalCard>
      </Box>
    </PageContainer>
  );
}

export default Page404;
